import { ActionIcons, Button, DirectionIcons, ScrollArea, Typography } from '@vt/vt-ui';
import React, { useState } from 'react';
import { actionsTypes } from '../../../config';
import formatTime from '../../../utilities/formatTime';
import { getOrderReason } from '../utils/getReason';
import { Notification } from '../../../utilities/alert';

const copyToClipboard = async (text, label = 'Text') => {
  try {
    await navigator.clipboard.writeText(text);
    Notification({
      title: 'Copied!',
      message: `${label} copied to clipboard`,
      type: 'success'
    });
  } catch (err) {
    console.error('Failed to copy: ', err);
    Notification({
      title: 'Copy Failed',
      message: 'Unable to copy to clipboard',
      type: 'danger'
    });
  }
};

const DetailBox = ({ title, data, expandedData }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="p-4 flex flex-col gap-4 text-[#E5E5E5] bg-[#202020] rounded-lg select-text">
      <Typography variant="h5" className="text-[#E5E5E5] !font-[700]">
        {title}
      </Typography>
      {data.map((item, index) => (
        <div className="flex justify-between select-text" key={index}>
          <Typography variant="body3" className="text-[#E5E5E5]">
            {item.title}
          </Typography>
          <Typography variant="h5" className="text-[#E5E5E5] !font-[700]">
            {item.value}
          </Typography>
        </div>
      ))}
      {expandedData && (
        <>
          <div className="w-full border-b border-[#404040]" />
          <div
            className="flex justify-between items-center cursor-pointer"
            onClick={() => setIsExpanded(!isExpanded)}>
            <Typography variant="h5" className="text-[#E5E5E5] !font-[700]">
              {expandedData.title}
            </Typography>
            {isExpanded ? (
              <DirectionIcons.Up className="w-4 h-4 fill-[#E5E5E5]" />
            ) : (
              <DirectionIcons.Down className="w-4 h-4 fill-[#E5E5E5]" />
            )}
          </div>
          {isExpanded &&
            expandedData.data.map((item, index) => (
              <div className="flex justify-between select-text" key={index}>
                <Typography variant="body3" className="text-[#E5E5E5]">
                  {item.title}
                </Typography>
                <Typography variant="h5" className="text-[#E5E5E5] !font-[700]">
                  {item.value}
                </Typography>
              </div>
            ))}
        </>
      )}
    </div>
  );
};

const Header = ({ symbol, action, volumeCurrent, volumeInitial, state }) => {
  return (
    <div className="mt-6 mb-2 select-text">
      <div className="flex justify-between p-2 text-[#e5e5e5] text-sm items-center">
        <div className="flex gap-2 items-center">
          <div className="w-[32px] content-center">
            <img
              src={`https://storage.googleapis.com/iux-storage/symbol/${symbol}.png`}
              alt="..."
              onError={({ currentTarget }) => {
                currentTarget.src = 'globe.svg';
              }}
              className="w-full rounded-[50%]"
            />
          </div>
          <div>
            <div className="flex items-center gap-1">
              <Typography variant="h5" className="text-[#e5e5e5]">
                {symbol}
              </Typography>
              {renderAction(action)}
            </div>
            <Typography variant="body4" className="text-[#9F9F9F]">
              {volumeInitial}/{volumeCurrent}
            </Typography>
          </div>
        </div>
        <Typography
          variant="h5"
          style={{
            color:
              state === 4
                ? '#24A759'
                : state === 2
                ? '#F3C006'
                : state === 6
                ? '#9F9F9F'
                : state === 5
                ? '#EF4533'
                : '',
            textAlign: 'right'
          }}>
          {' '}
          {state === 4
            ? 'Filled'
            : state === 2
            ? 'Canceled'
            : state === 6
            ? 'Expired'
            : state === 5
            ? 'Rejected'
            : ''}
        </Typography>
      </div>
    </div>
  );
};

const renderAction = (x) => {
  switch (x) {
    default:
      return (
        <div
          style={{ color: actionsTypes[x]?.color, marginLeft: '4px' }}
          className="font-bold text-sm">
          {actionsTypes[x]?.name}
        </div>
      );
  }
};

const OrderDetail = ({ onBack, data, openChart }) => {
  return (
    <>
      <div className="bg-[#151515] h-full w-full p-4 border-r border-[#232323]">
        <div className="text-[#e5e5e5] text-md font-bold flex justify-between items-center">
          <DirectionIcons.Left className="w-5 h-5 fill-[#9F9F9F] cursor-pointer" onClick={onBack} />
          <Typography variant="h4" className="text-[#E5E5E5]">
            Details
          </Typography>
          <div className="h-full flex cursor-pointer" onClick={onBack}>
            <svg
              className="m-auto history-btn-action"
              width="11"
              height="11"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M0.781396 16.0001C0.626858 16.0001 0.475783 15.9543 0.347281 15.8685C0.218778 15.7826 0.118621 15.6606 0.0594776 15.5178C0.000334661 15.3751 -0.0151369 15.218 0.0150198 15.0664C0.0451766 14.9148 0.119607 14.7756 0.228896 14.6664L14.6664 0.228853C14.8129 0.0823209 15.0117 0 15.2189 0C15.4261 0 15.6249 0.0823209 15.7714 0.228853C15.9179 0.375385 16.0002 0.574125 16.0002 0.781353C16.0002 0.988581 15.9179 1.18732 15.7714 1.33385L1.3339 15.7714C1.26141 15.844 1.17528 15.9016 1.08047 15.9408C0.985653 15.9801 0.884016 16.0002 0.781396 16.0001Z"
                fill="#9f9f9f"
              />
              <path
                d="M15.2189 16.0001C15.1162 16.0002 15.0146 15.9801 14.9198 15.9408C14.825 15.9016 14.7388 15.844 14.6664 15.7714L0.228853 1.33385C0.0823209 1.18732 0 0.988581 0 0.781353C0 0.574125 0.0823209 0.375385 0.228853 0.228853C0.375385 0.0823209 0.574125 0 0.781353 0C0.988581 0 1.18732 0.0823209 1.33385 0.228853L15.7714 14.6664C15.8806 14.7756 15.9551 14.9148 15.9852 15.0664C16.0154 15.218 15.9999 15.3751 15.9408 15.5178C15.8816 15.6606 15.7815 15.7826 15.653 15.8685C15.5245 15.9543 15.3734 16.0001 15.2189 16.0001Z"
                fill="#9f9f9f"
              />
            </svg>
          </div>
        </div>
        <Header
          symbol={data?.symbol}
          action={data?.type}
          volumeCurrent={(data?.volumeInitial / 10000 - data?.volumeCurrent / 10000).toFixed(2)}
          volumeInitial={(data?.volumeInitial / 10000).toFixed(2)}
          state={data?.state}
        />
        <ScrollArea className="h-[calc(100vh-205px)]">
          <div className="flex flex-col gap-4 relative">
            <DetailBox
              title="Price"
              data={[
                {
                  title: 'Price',
                  value:
                    data?.priceOrder === 0 ? 'at market' : data?.priceOrder?.toFixed(data?.digits)
                },
                {
                  title: 'Stop Loss',
                  value: data?.priceSl ? data?.priceSl?.toFixed(data?.digits) : '-'
                },
                {
                  title: 'Take Profit',
                  value: data?.priceTp ? data?.priceTp?.toFixed(data?.digits) : '-'
                }
              ]}
              expandedData={{
                title: 'Transaction Details',
                data: [
                  {
                    title: 'Open Reason',
                    value: getOrderReason(data?.reason) || '-'
                  },
                  {
                    title: 'Comment',
                    value: data?.comment || '-'
                  }
                ]
              }}
            />
            <DetailBox
              title="Time"
              data={[
                {
                  title: 'Open Time',
                  value: data?.timeSetup ? formatTime(data?.timeSetup || 0) : '-'
                },
                {
                  title: 'Placed Time',
                  value: data?.timeDone ? formatTime(data?.timeDone || 0) : '-'
                },
                {
                  title: 'Expired Time',
                  value: data?.timeExpiration ? formatTime(data?.timeExpiration || 0) : '-'
                }
              ]}
              expandedData={{
                title: 'Local Time',
                data: [
                  {
                    title: 'Open Time',
                    value: data?.timeSetup ? (
                      <>
                        <div className="text-[#E5E5E5] text-end">
                          {formatTime(data?.timeSetup || 0, true)}
                        </div>
                        <div className="text-end">(GMT+7)</div>
                      </>
                    ) : (
                      '-'
                    )
                  },
                  {
                    title: 'Placed Time',
                    value: data?.timeDone ? (
                      <>
                        <div className="text-[#E5E5E5] text-end">
                          {formatTime(data?.timeDone || 0, true)}
                        </div>
                        <div className="text-end">(GMT+7)</div>
                      </>
                    ) : (
                      '-'
                    )
                  },
                  {
                    title: 'Expired Time',
                    value: data?.timeExpiration ? (
                      <>
                        <div className="text-[#E5E5E5] text-end">
                          {formatTime(data?.timeExpiration || 0, true)}
                        </div>
                        <div className="text-end">(GMT+7)</div>
                      </>
                    ) : (
                      '-'
                    )
                  }
                ]
              }}
            />
            <DetailBox
              title="Additional Information"
              data={[
                {
                  title: 'Order ID',
                  value: (
                    <div
                      className="flex items-center gap-1 cursor-pointer select-text"
                      onClick={() => copyToClipboard(data?.order, 'Order ID')}>
                      {data?.order} <ActionIcons.Clone className="w-4 h-4 fill-[#E5E5E5]" />
                    </div>
                  )
                },
                {
                  title: 'Position ID',
                  value: data?.positionId ? (
                    <div
                      className="flex items-center gap-1 cursor-pointer select-text"
                      onClick={() => copyToClipboard(data?.positionId, 'Position ID')}>
                      {data?.positionId} <ActionIcons.Clone className="w-4 h-4 fill-[#E5E5E5]" />
                    </div>
                  ) : (
                    '-'
                  )
                }
              ]}
            />
          </div>
          <div className="pt-6 !sticky bottom-0 bg-[#151515]">
            <Button variant="primary" className="w-full" onClick={() => openChart(data)}>
              Chart
            </Button>
          </div>
        </ScrollArea>
      </div>
    </>
  );
};

export default OrderDetail;
